/**
 * Dimensional pivot for ONE Plan View — the selected org node(s)' consolidated budget shaped by that
 * view's row dimensions (Header Type outermost, then the view's dims), with every measure of the view
 * (Volym/Pris/Värde, drivers and computed measures such as a margin and its share). Rendered as a nested
 * MUI table with group subtotals + collapse, matching the "By period & node" tables (read-only). The page
 * shows one of these per Plan View tab. Every figure — leaf, group and footer — is the server's total by
 * the cell aggregation of Nexo-Formula-Language.md §9.3; this component totals nothing itself.
 */

import { useMemo, useState } from 'react';
import {
  Alert, Box, CircularProgress, Paper, Table, TableBody, TableCell, TableFooter, TableHead, TableRow, Typography,
} from '@mui/material';
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { apiClient } from '@/lib/apiClient';
import { formatNumber } from '@/lib/locale';
import { buildRollupTree, isHiddenByCollapse, rollupPath, RollupTreeRow } from './rollupTree';
import { measureFormatOptions } from './planEntryRows';
import { RollupUnfilteredTotalRow, useFilteredTotalLabel } from './RollupFilteredTotal';
import { EMPTY_SHAPING, shapingQueryKey, shapingRequestFields, type RollupShaping } from './planRollupShaping';

interface PivotKey { valueCode: string; valueName: string }
/** One per-unit quantity of a total — sparse, matched to the DTO's quantityColumns by unit (null id = unitless). */
interface PivotUnitQuantity { unitOfMeasureId?: string | null; quantity: number }
/** One measure's total (§9.3): null where it has none; a quantity with its sum per unit. */
interface PivotMeasureTotal { value?: number | null; quantities?: PivotUnitQuantity[] }
type PivotTotals = Record<string, PivotMeasureTotal>;
interface PivotRow { key: PivotKey[]; measures: PivotTotals }
/** One level of the row tree by its value names, outermost first, leaves included. */
interface PivotGroup { path: string[]; measures: PivotTotals }
interface PivotAxis { dimensionDefinitionId: string; code: string; name: string }
/** A unit actually present in the scope — one Volym column each (data-driven, spec §12.4). */
interface PivotQuantityColumn {
  unitOfMeasureId?: string | null;
  unitCode?: string | null;
  unitName?: string | null;
  totalQuantity: number;
}
interface PivotMeasure {
  /** The key of the measure's totals. */
  code: string;
  label: string;
  unit?: string | null;
  storageBinding: string;
  sortOrder: number;
  /** The measure's value type — the fallback when displayDecimals is not configured. */
  valueType?: string;
  /** Decimals configured on the measure in the Plan View admin; null = the value type's default. */
  displayDecimals?: number | null;
}
interface PlanRollupPivotDto {
  planViewId?: string | null;
  axes: PivotAxis[];
  measures: PivotMeasure[];
  quantityColumns: PivotQuantityColumn[];
  rows: PivotRow[];
  groups?: PivotGroup[];
  /** The footer: every measure's total over all rows. */
  totals?: PivotTotals;
  grandTotal: number;
  /** Whether a filter or period range narrowed this read — the server's answer, not the request's (§11). */
  isShaped: boolean;
  warnings: string[];
}

// POST like every other rollup read: the filter AST has no upper bound and does not belong in a request
// line, and every tab must narrow the same way or the tabs stop being comparable (§6, §9.1).
const PIVOT_ENDPOINT = '/api/finance/planbudgetrollup/pivot';

// A rendered column: one per measure of the Plan View (labels, units and decimals are the view's
// declaration, never this component's). The quantity stored on the line fans out into one column per unit
// actually present in the scope (spec §12.4 — `key` carries the unit so React keys stay stable as units come
// and go); every other measure is one column. The internal price (INTERNAL_REVENUE_LINE) is a Plan Entry
// display column whose figure is another line's: it gets none.
type PivotCol =
  | { kind: 'quantity'; key: string; code: string; unitId: string | null; header: string; format: Intl.NumberFormatOptions }
  | { kind: 'value'; key: string; code: string; header: string; format: Intl.NumberFormatOptions };
// Labels are translation keys — resolved where the fallback measures are used (server measures
// arrive with their own display labels and are rendered untranslated).
const DEFAULT_MEASURES: PivotMeasure[] = [
  { code: 'VOLUME', label: 'Finance.PlanRollup.MeasureVolume', storageBinding: 'PLANLINE_QUANTITY', sortOrder: 1, valueType: 'QUANTITY' },
  { code: 'PRICE', label: 'Finance.PlanRollup.MeasurePrice', storageBinding: 'PLANLINE_UNITPRICE', sortOrder: 2, valueType: 'RATE' },
  { code: 'VALUE', label: 'Finance.PlanRollup.MeasureValue', storageBinding: 'PLANLINE_AMOUNT', sortOrder: 3, valueType: 'AMOUNT' },
];

/** A measure's figure in one column: its value, or a quantity's sum in the column's unit. */
function valueIn(totals: PivotTotals | undefined, col: PivotCol): number | null {
  const total = totals?.[col.code];
  if (col.kind === 'quantity') {
    return total?.quantities?.find((q) => (q.unitOfMeasureId ?? null) === col.unitId)?.quantity ?? null;
  }
  return total?.value ?? null;
}

const fmt = (v: number | null | undefined, options: Intl.NumberFormatOptions) =>
  v == null ? '' : formatNumber(v, options);

interface Props {
  versionId: string;
  orgUnitIds: string[];
  excludeOrgUnitIds?: string[];
  inclusion: 'SUBMITTED' | 'ALL';
  planViewId: string;
  /** The page-level filter and period window — every tab narrows on the same one (§9.1). */
  shaping?: RollupShaping;
  /** The page's unshaped consolidated total, printed beside a filtered one as the reference (§11). */
  unfilteredTotal?: number;
}

export function PlanRollupPivot({
  versionId,
  orgUnitIds,
  excludeOrgUnitIds = [],
  inclusion,
  planViewId,
  shaping = EMPTY_SHAPING,
  unfilteredTotal,
}: Props) {
  const { t } = useTranslation();
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const filteredLabel = useFilteredTotalLabel();

  const { data, isFetching, isError, error } = useQuery<PlanRollupPivotDto, Error>({
    queryKey: [
      'planRollupPivot', versionId, [...orgUnitIds].sort(), [...excludeOrgUnitIds].sort(), inclusion, planViewId,
      shapingQueryKey(shaping),
    ],
    queryFn: async () => {
      return (await apiClient.post<PlanRollupPivotDto>(PIVOT_ENDPOINT, {
        planVersionId: versionId,
        orgUnitIds,
        excludeOrgUnitIds,
        inclusion,
        planViewId,
        ...shapingRequestFields(shaping),
      })).data;
    },
    enabled: !!versionId && !!planViewId && orgUnitIds.length > 0,
  });

  const axes = data?.axes ?? [];
  const usingFallbackMeasures = !data?.measures?.length;
  const measures = data?.measures?.length ? data.measures : DEFAULT_MEASURES;
  const quantityColumns = useMemo(
    () => (data?.quantityColumns ?? []).map((c) => ({ ...c, unitId: c.unitOfMeasureId ?? null })),
    [data],
  );
  const measureCols = useMemo(
    () => {
      // The amount measure's free-text unit ("kr") — the numerator of a derived rate header.
      const valueUnit = measures.find((m) => m.storageBinding === 'PLANLINE_AMOUNT')?.unit ?? '';
      return measures.flatMap((m): PivotCol[] => {
        if (m.storageBinding === 'INTERNAL_REVENUE_LINE') return [];
        const label = usingFallbackMeasures ? t(m.label) : m.label;
        // The measure declares its own decimals (Plan View admin), with the value type's default
        // behind it — resolved by the SAME helper the entry grid uses, so the two screens can
        // never disagree about how a number is written.
        const format = measureFormatOptions({ valueType: m.valueType, displayDecimals: m.displayDecimals });
        if (m.storageBinding === 'PLANLINE_QUANTITY') {
          // One column per unit actually present (spec §12.4). Header precedence: the row data's unit
          // code; for the unitless bucket the measure's free-text unit when it is the sole column
          // (legacy views seeded the unit as text while their lines carry none), else an explicit
          // "no unit" marker — the bucket holds quantities whose unit was never stamped, any unit at
          // all, and a bare label would pose as the plain quantity column beside the real units. No
          // columns (empty scope) keeps one column with today's header so the empty table still has
          // its layout.
          if (quantityColumns.length === 0) {
            return [{ kind: 'quantity', key: m.code, code: m.code, unitId: null, header: m.unit ? `${label} (${m.unit})` : label, format }];
          }
          return quantityColumns.map((c) => {
            const unitText = c.unitCode
              ?? (quantityColumns.length === 1 && m.unit ? m.unit : null)
              ?? t('Finance.PlanRollup.NoUnitColumn', { defaultValue: 'no unit' });
            return {
              kind: 'quantity' as const,
              key: `${m.code}__${c.unitId ?? 'unitless'}`,
              code: m.code,
              unitId: c.unitId,
              header: `${label} (${unitText})`,
              format,
            };
          });
        }
        if (m.storageBinding === 'PLANLINE_UNITPRICE') {
          // Single rate column (per-unit weighted averages are OQ-12): free text wins where seeded;
          // else derive "kr/m3f" from the amount measure's unit + the single real unit column; else bare.
          const soleCode = quantityColumns.length === 1 ? quantityColumns[0].unitCode : null;
          const derived = !m.unit && soleCode && valueUnit ? `${valueUnit}/${soleCode}` : m.unit;
          return [{ kind: 'value', key: m.code, code: m.code, header: derived ? `${label} (${derived})` : label, format }];
        }
        return [{ kind: 'value', key: m.code, code: m.code, header: m.unit ? `${label} (${m.unit})` : label, format }];
      });
    },
    [measures, quantityColumns, usingFallbackMeasures, t],
  );

  const allRows = useMemo(() => buildRollupTree((data?.rows ?? []).map((r) => r.key.map((k) => k.valueName))), [data]);
  // Every level's totals by its path — leaves included — as the server totalled them from their lines.
  const totalsByPath = useMemo(
    () => new Map((data?.groups ?? []).map((g) => [rollupPath(g.path), g.measures])),
    [data],
  );

  const visibleRows = allRows.filter((r) => !isHiddenByCollapse(r.path, collapsed));
  const valueOf = (row: RollupTreeRow, col: PivotCol) => valueIn(totalsByPath.get(row.path), col);
  const footerValueOf = (col: PivotCol) => valueIn(data?.totals, col);

  const toggle = (path: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });

  if (isFetching) return <Box sx={{ p: 3, textAlign: 'center' }}><CircularProgress size={24} /></Box>;
  if (isError) {
    return (
      <Alert severity="error">
        {error?.message ?? t('Finance.PlanRollup.PivotLoadFailed', { defaultValue: 'Failed to load the dimensional pivot.' })}
      </Alert>
    );
  }
  if (!data) return null;

  return (
    <Box>
      {data.warnings.map((w, i) => <Alert key={i} severity="warning" sx={{ mb: 1 }}>{w}</Alert>)}
      {axes.length <= 1 && (
        <Alert severity="info" sx={{ mb: 1 }}>
          {t('Finance.PlanRollup.PivotNoRowDims', {
            defaultValue: 'This Plan View has no row dimensions to pivot by — showing header-type totals only.',
          })}
        </Alert>
      )}
      <Paper variant="outlined" sx={{ maxWidth: 900 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>
                {axes.map((a) => a.name).join(' › ') || t('Finance.PlanEntry.Group', { defaultValue: 'Group' })}
              </TableCell>
              {measureCols.map((mc) => (
                <TableCell key={mc.key} align="right">{mc.header}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleRows.map((row) => (
              <TableRow key={row.path} hover>
                <TableCell sx={{ pl: row.depth * 2.5 + 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontWeight: row.isGroup ? 600 : 400 }}>
                    {row.isGroup ? (
                      <Box component="span" sx={{ display: 'inline-flex', cursor: 'pointer' }} onClick={() => toggle(row.path)}>
                        {collapsed.has(row.path) ? <KeyboardArrowRight fontSize="small" /> : <KeyboardArrowDown fontSize="small" />}
                      </Box>
                    ) : (
                      <Box component="span" sx={{ width: 20, display: 'inline-block' }} />
                    )}
                    <span>{row.label}{row.isGroup ? ` (${row.childCount})` : ''}</span>
                  </Box>
                </TableCell>
                {measureCols.map((mc) => (
                  <TableCell key={mc.key} align="right" sx={{ fontWeight: row.isGroup ? 600 : 400 }}>
                    {fmt(valueOf(row, mc), mc.format)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
            {visibleRows.length === 0 && (
              <TableRow>
                <TableCell colSpan={measureCols.length + 1}>
                  <Typography variant="body2" color="text.secondary">
                    {t('Finance.PlanRollup.PivotEmpty', { defaultValue: 'No budget under this selection for this Plan View.' })}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>
                {filteredLabel(t('Finance.PlanRollup.Total', { defaultValue: 'Total' }), data.isShaped)}
              </TableCell>
              {measureCols.map((mc) => (
                <TableCell key={mc.key} align="right" sx={{ fontWeight: 600 }}>
                  {fmt(footerValueOf(mc), mc.format)}
                </TableCell>
              ))}
            </TableRow>
            <RollupUnfilteredTotalRow
              isShaped={data.isShaped}
              unfilteredTotal={unfilteredTotal}
              colSpan={measureCols.length + 1}
            />
          </TableFooter>
        </Table>
      </Paper>
    </Box>
  );
}

export default PlanRollupPivot;
